function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

/* global $ */
/*
 This class implements interaction with UDF-compatible datafeed.

 See UDF protocol reference at
 https://github.com/tradingview/charting_library/wiki/UDF
 */
import { APEX } from 'alphapoint';
import { getGateway } from '../../helpers/wsHelper';
import { convertLabelToTickerInterval } from '../../helpers/tickerHelper';
var ChartAPI = {};
var UDFCompatibleDatafeed = function UDFCompatibleDatafeed(datafeedURL, protocolVersion, customGateway, currentTimezone, getDecimalPlaces) {
  var _this = this;
  _classCallCheck(this, UDFCompatibleDatafeed);
  this.buildBar = function (barData) {
    var barValue = {
      time: barData[0],
      high: barData[1],
      low: barData[2],
      open: barData[3],
      close: barData[4],
      volume: barData[5]
    };
    return barValue;
  };
  this.getBars = function (symbolInfo, resolution, rangeStartDate, rangeEndDate, onDataCallback) {
    var that = _this;
    if (rangeStartDate > 0 && ('' + rangeStartDate).length > 10) {
      throw new Error('Got a JS time instead of Unix one. rangeStartDate: ' + rangeStartDate + ', rangeEndDate: ' + rangeEndDate);
    }
    var interval = convertLabelToTickerInterval(resolution);
    var newIntervalMs = interval * 1000;
    var req = {};
    req.OMSId = symbolInfo.omsId;
    req.InstrumentId = symbolInfo.instrumentId;
    req.Interval = interval; // interval is seconds
    req.IncludeLastCount = 5000;
    _this.subscriptionIntervalMs = newIntervalMs;
    that.ws.RPCCall('SubscribeTicker', req, function () {
      var data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      data = JSON.parse(data.o);
      var noData = !data.length;
      var bars = [];
      var barsCount = data.length;
      for (var i = 0; i < barsCount; ++i) {
        var barData = data[i];
        var barValue = that.buildBar(barData);
        bars.push(barValue);
      }
      if (barsCount > 0) {
        _this.gotBars = symbolInfo.name;
        _this.lastBar = bars[bars.length - 1];
      } else {
        _this.lastBar = null;
      }
      onDataCallback(bars, {
        version: that._protocolVersion,
        noData: noData,
        nextTime: data.nb || data.nextTime || null
      });

      // Prevent looping when exiting a specified period of time
      var filteredBars = bars.filter(function (item) {
        return item.time >= rangeStartDate * 1000 && item.time <= rangeEndDate * 1000;
      });
      if (filteredBars.length === 0) {
        onDataCallback([], {
          version: that._protocolVersion,
          noData: true,
          nextTime: null
        });
      }
    });
  };
  this.onMessage = function (event) {
    var synchroCallBack = _this._amsynchro[event.i];
    if (synchroCallBack === undefined) {
      if (event.n === 'TickerDataUpdateEvent') {
        var data = JSON.parse(event.o);
        if (_this.callback !== undefined && _this.callback !== null) {
          var newBar = _this.buildBar(data[0]);
          if (_this.lastBar !== null) {
            if (newBar.time - _this.subscriptionIntervalMs < _this.lastBar.time) {
              newBar.time = _this.lastBar.time;
            }
            _this.callback(newBar);
            _this.lastBar = newBar;
          }
        }
      }
    } else if (event.o === '') {
      synchroCallBack({});
    } else if (synchroCallBack != null) {
      synchroCallBack(JSON.parse(event.o));
    }
  };
  this.defaultConfiguration = function () {
    return {
      supports_search: true,
      supports_group_request: false,
      supported_resolutions: [1, 5, 15, 30, 60, 360, 720, 'D', 'W', 'M'],
      supports_marks: false,
      supports_timescale_marks: false
    };
  };
  this.getServerTime = function (callback) {
    if (_this._configuration.supports_time) {
      _this._send(_this._datafeedURL + '/time', {}).done(function (response) {
        callback(+response);
      }).fail(function () {});
    }
  };
  this.on = function (event, callback) {
    if (!Object.prototype.hasOwnProperty.call(_this._callbacks, event)) {
      _this._callbacks[event] = [];
    }
    _this._callbacks[event].push(callback);
    return _this;
  };
  this.onInitialized = function () {
    _this._initializationFinished = true;
    _this._fireEvent('initialized');
  };
  this.onReady = function (callback) {
    var that = _this;
    if (_this._configuration) {
      setTimeout(function () {
        callback(that._configuration);
      }, 0);
    } else {
      _this.on('configuration_ready', function () {
        callback(that._configuration);
      });
    }
  };
  this.resolveSymbol = function (symbolName, onSymbolResolvedCallback, onResolveErrorCallback) {
    var symbol = {};
    var instrument = _this.instrumentsArray.filter(function (i) {
      return i.Symbol === symbolName;
    })[0];
    if (instrument) {
      // might be DEPRICATED, but anyway can help
      // https://github.com/alphapoint/charting_library_wiki
      var currentDecimalPlaces = _this._getDecimalPlaces(instrument.PriceIncrement) || 2;
      symbol.omsId = instrument.OMSId;
      symbol.name = instrument.Symbol;
      symbol.full_name = instrument.Symbol;
      symbol.description = instrument.Symbol;
      symbol.instrumentId = instrument.InstrumentId;
      symbol.ticker = instrument.Symbol;
      symbol.minmov = 1;
      symbol.pricescale = Math.pow(10, currentDecimalPlaces); // responsible for decimal places for axis`s numbers
      symbol.timezone = _this._timezone;
      symbol.session = '24x7';
      symbol.has_intraday = true;
      setTimeout(function () {
        onSymbolResolvedCallback(symbol);
      }, 0);
    } else if (_this._symbolRetries < 10) {
      _this._symbolRetries++;
      console.log('Symbol resolve retry #' + _this._symbolRetries);
      setTimeout(function () {
        _this.resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback);
      }, 100);
    } else {
      onResolveErrorCallback('Could not resolve ' + symbolName + ' after ' + _this._symbolRetries + ' retries');
    }
  };
  this.searchSymbolsByName = function () {};
  this.searchSymbols = function (searchString, exchange, type, onResultReadyCallback) {
    var MAX_SEARCH_RESULTS = 30;
    if (!_this._configuration) {
      onResultReadyCallback([]);
      return;
    }
    if (_this._configuration.supports_search) {
      _this._send(_this._datafeedURL + '/search', {
        limit: MAX_SEARCH_RESULTS,
        query: searchString.toUpperCase(),
        type: type,
        exchange: exchange
      }).done(function (response) {
        var data = JSON.parse(response);
        for (var i = 0; i < data.length; ++i) {
          if (!data[i].params) {
            data[i].params = [];
          }
        }
        if (typeof data.s === 'undefined' || data.s !== 'error') {
          onResultReadyCallback(data);
        } else {
          onResultReadyCallback([]);
        }
      }).fail(function (reason) {
        onResultReadyCallback([]);
        throw new Error(reason);
      });
    } else {
      if (!_this._symbolSearch) {
        throw new Error('Datafeed error: inconsistent configuration (symbol search)');
      }
      var searchArgument = {
        searchString: searchString,
        exchange: exchange,
        type: type,
        onResultReadyCallback: onResultReadyCallback
      };
      if (_this._initializationFinished) {
        _this._symbolSearch.searchSymbols(searchArgument, MAX_SEARCH_RESULTS);
      } else {
        var that = _this;
        _this.on('initialized', function () {
          that._symbolSearch.searchSymbols(searchArgument, MAX_SEARCH_RESULTS);
        });
      }
    }
  };
  this.subscribeBars = function (symbolInfo, resolution, onRealtimeCallback) {
    _this.callback = onRealtimeCallback;
    _this.unsubscribeReqObj.OMSId = symbolInfo.omsId;
    _this.unsubscribeReqObj.InstrumentId = symbolInfo.instrumentId;
  };
  this.unsubscribeBars = function () {
    var req = _this.unsubscribeReqObj;
    _this.ws.RPCCall('UnsubscribeTicker', req);
  };
  this._setupWithConfiguration = function (configurationData) {
    _this._configuration = configurationData;
    if (!_this._configuration.exchanges) {
      _this._configuration.exchanges = [];
    }

    // @obsolete; remove in 1.5
    var supportedResolutions = _this._configuration.supported_resolutions || _this._configuration.supportedResolutions;
    _this._configuration.supported_resolutions = supportedResolutions;

    // @obsolete; remove in 1.5
    var symbolsTypes = _this._configuration.symbols_types || _this._configuration.symbolsTypes;
    _this._configuration.symbols_types = symbolsTypes;
    if (!_this._configuration.supports_search && !_this._configuration.supports_group_request) {
      throw new Error('Unsupported datafeed configuration. Must either support search, or support group request');
    }
    if (!_this._configuration.supports_search) {
      _this._symbolSearch = new ChartAPI.SymbolSearchComponent(_this);
    }
    if (_this._configuration.supports_group_request) {
      // this component will call onInitialized() by itself
      // this._symbolsStorage = new ChartAPI.SymbolsStorage(this);
    } else {
      _this.onInitialized();
    }
    _this._fireEvent('configuration_ready');
    _this._logMessage('Initialized with ' + JSON.stringify(configurationData));
  };
  this._initialize = function () {
    _this._setupWithConfiguration(_this.defaultConfiguration());
    _this.ws.ws.filter(function (x) {
      return x.n === 'TickerDataUpdateEvent';
    }).subscribe(function (data) {
      data = data.o;
      if (_this.callback !== null) {
        var newBar = _this.buildBar(data[0]);
        if (_this.lastBar !== null && newBar && typeof newBar.time !== 'string') {
          if (newBar.time - _this.subscriptionIntervalMs < _this.lastBar.time) {
            newBar.time = _this.lastBar.time;
          }
          _this.callback(newBar);
          _this.lastBar = newBar;
        }
      }
    });
  };
  this._fireEvent = function (event, argument) {
    if (Object.prototype.hasOwnProperty.call(_this._callbacks, event)) {
      var callbacksChain = _this._callbacks[event];
      for (var i = 0; i < callbacksChain.length; ++i) {
        callbacksChain[i](argument);
      }
      _this._callbacks[event] = [];
    }
  };
  this._logMessage = function (message) {
    if (_this._enableLogging) {
      console.log(message, new Date());
    }
  };
  this._send = function (url, params) {
    var request = url;
    if (params) {
      for (var i = 0; i < Object.keys(params).length; ++i) {
        var key = Object.keys(params)[i];
        var value = encodeURIComponent(params[key]);
        request += (i === 0 ? '?' : '&') + key + '=' + value;
      }
    }
    _this._logMessage('New request: ' + request);
    return $.ajax({
      type: 'GET',
      url: request,
      contentType: 'text/plain'
    });
  };
  this._amsynchro = {};
  this._callbacks = {};
  this._configuration = undefined;
  this._datafeedURL = datafeedURL;
  this._enableLogging = false;
  this._initializationFinished = false;
  this._historyURL = '/history';
  this._protocolVersion = protocolVersion || 2;
  this._symbolSearch = null;
  this._symbolsStorage = null;
  this._symbolResolveURL = '/symbols';
  this._symbolRetries = 0;
  this._timezone = currentTimezone;
  this._getDecimalPlaces = getDecimalPlaces.bind(this);
  this.callback = null;
  this.connected = false;
  this.lastBar = null;
  this.unsubscribeReqObj = {};
  this.gotBars = '';
  this.subscriptionIntervalMs = 0;
  var onopen = this._initialize.bind(this);
  this.ws = new APEX(customGateway ? customGateway : getGateway(), {
    onopen: onopen
  });
  this.ws.getInstruments(function (data) {
    _this.instrumentsArray = JSON.parse(data.o);
  });
  this.ws.ws.filter(function (x) {
    return x.n === 'TickerDataUpdateEvent';
  }).subscribe(function (response) {
    _this.onMessage(response);
  });
}
// BEWARE: this function does not consider symbol's exchange
;
ChartAPI.UDFCompatibleDatafeed = UDFCompatibleDatafeed;
export default ChartAPI;